class MrBookingInputs extends HTMLElement {
	#changeHandler = ( e: Event ) => {
		const {
			dateInputStart, dateInputEnd, select,
		} = this.getElements();
		if ( !dateInputStart || !dateInputEnd ) {
			return;
		}

		if ( select && e.target === select ) {
			this.setEndDateForLengthOfStay( dateInputStart, dateInputEnd, select );
		} else if ( select && dateInputStart && e.target === dateInputStart ) {
			this.setEndDateForLengthOfStay( dateInputStart, dateInputEnd, select );
		}

		this.setValidity( dateInputStart, dateInputEnd );
	};

	connectedCallback() {
		this.addEventListener( 'change', this.#changeHandler );

		requestAnimationFrame( () => {
			const {
				dateInputStart, dateInputEnd, select,
			} = this.getElements();
			if ( !dateInputStart || !dateInputEnd ) {
				return;
			}

			if ( select ) {
				this.setupLengthOfStaySelectMode( dateInputStart, dateInputEnd, select );

				return;
			}

			this.setupDateRangeMode( dateInputStart, dateInputEnd );
		} );
	}

	disconnectedCallback() {
		this.removeEventListener( 'change', this.#changeHandler );
	}

	private getElements(): { dateInputStart: HTMLInputElement|null, dateInputEnd: HTMLInputElement|null, select: HTMLSelectElement|null } {
		const inputs = Array.from( this.querySelectorAll<HTMLInputElement>( 'input[type="date"]' ) );
		const select = this.querySelector( 'select' );

		return {
			dateInputStart: inputs.find( ( x ) => {
				return x.name === 'checkInDate';
			} ) ?? null,
			dateInputEnd: inputs.find( ( x ) => {
				return x.name === 'checkOutDate';
			} ) ?? null,
			select: select,
		};
	}

	private getLatestBookableDate(): Date {
		const latestBookableDate = new Date();

		const attr = this.getAttribute( 'data-latest-bookable-date-in-years-from-now' );
		if ( !attr ) {
			latestBookableDate.setFullYear( latestBookableDate.getFullYear() + 10 );

			return latestBookableDate;
		}

		const parsed = parseInt( attr, 10 );
		if ( isNaN( parsed ) || !parsed ) {
			latestBookableDate.setFullYear( latestBookableDate.getFullYear() + 10 );

			return latestBookableDate;
		}
		latestBookableDate.setFullYear( latestBookableDate.getFullYear() + parsed );

		return latestBookableDate;
	}

	setupDateRangeMode( dateInputStart: HTMLInputElement, dateInputEnd: HTMLInputElement ) {
		{
			const now = new Date();

			dateInputStart.setAttribute( 'min', this.formatDateForFormField( now ) );
			dateInputStart.setAttribute( 'max', this.formatDateForFormField( this.getLatestBookableDate() ) );
			dateInputStart.value = this.formatDateForFormField( now );
		}

		{
			const tomorrow = new Date();
			tomorrow.setDate( tomorrow.getDate() + 1 );

			dateInputEnd.setAttribute( 'min', this.formatDateForFormField( tomorrow ) );
			dateInputEnd.setAttribute( 'max', this.formatDateForFormField( this.getLatestBookableDate() ) );
			dateInputEnd.value = this.formatDateForFormField( tomorrow );
		}
	}

	setupLengthOfStaySelectMode( dateInputStart: HTMLInputElement, dateInputEnd: HTMLInputElement, select: HTMLSelectElement ) {
		{
			const now = new Date();

			dateInputStart.setAttribute( 'min', this.formatDateForFormField( now ) );
			dateInputStart.setAttribute( 'max', this.formatDateForFormField( this.getLatestBookableDate() ) );
			dateInputStart.value = this.formatDateForFormField( now );
		}

		this.setEndDateForLengthOfStay( dateInputStart, dateInputEnd, select );
	}

	setEndDateForLengthOfStay( dateInputStart: HTMLInputElement, dateInputEnd: HTMLInputElement, select: HTMLSelectElement ) {
		const duration = parseInt( select.value, 10 );
		const start = dateInputStart.valueAsDate;

		if ( !start ) {
			return;
		}

		const end = new Date( start.getTime() );
		end.setDate( start.getDate() + duration );

		dateInputEnd.value = this.formatDateForFormField( end );
	}

	setValidity( dateInputStart: HTMLInputElement, dateInputEnd: HTMLInputElement ) {
		if ( dateInputEnd.hidden ) {
			return;
		}

		const start = dateInputStart.valueAsDate;
		const end = dateInputEnd.valueAsDate;

		if ( !start || !end ) {
			// required-ness is already handled natively
			return;
		}

		if ( end <= start ) {
			// TODO: make this configurable in site options and pass it as a data attribute?
			dateInputEnd.setCustomValidity( 'Please fill in a date after the check-in date.' );

			return;
		}

		// Reset custom message (Needed when a user first put in a wrong date and then puts in a correct date. )
		dateInputEnd.setCustomValidity( '' );
	}

	formatDateForFormField( date: Date ): string {
		return date.toISOString().split( 'T' )[0];
	}
}

window.customElements.define( 'mr-booking-inputs', MrBookingInputs );
